"use client";

import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import type { PostHog } from "posthog-js";

// ---------------------------------------------------------------------------
// Bootstrap — runs once when the component mounts in the browser.
// We use the /ingest proxy defined in next.config.ts so analytics calls are
// routed through your own domain and not blocked by ad-blockers.
//
// posthog-js (~178 KiB minified — confirmed via grep on the production
// chunk) is loaded via dynamic import so it lands in its own lazy chunk
// instead of riding into the critical bundle that /login, /signup, and
// every marketing page download synchronously. The hook + context wrapper
// from posthog-js/react are left as static imports because they are tiny
// (~5 KiB) and React needs the Context object available at provider
// mount time.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Identify — reads the user_id and site_id that the server sets as plain
// cookies after signup/login, then calls posthog.identify() so every
// subsequent event is linked to the real user profile in PostHog.
// ---------------------------------------------------------------------------

function PostHogIdentify() {
  const ph = usePostHog();

  useEffect(() => {
    if (!ph) return;

    function getCookie(name: string): string | undefined {
      const match = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)")
      );
      return match ? decodeURIComponent(match[1]) : undefined;
    }

    const userId = getCookie("user_id");
    if (!userId) return;

    const siteId = getCookie("site_id");

    // Only call identify once per session — PostHog deduplicates internally
    // but skipping the call when already identified avoids a redundant event.
    if (ph.get_distinct_id() === userId) return;

    ph.identify(userId, {
      // These properties appear on the Person record in PostHog.
      site_id: siteId ?? null,
    });
  }, [ph]);

  return null;
}

// ---------------------------------------------------------------------------
// Provider — wraps the entire app so any component can call usePostHog().
//
// On mount, defers the dynamic import of posthog-js to requestIdleCallback
// (with a setTimeout(0) fallback for Safari, which doesn't ship the API).
// The PHProvider only wraps `children` once the client instance is loaded
// and initialized — before that we render children unwrapped so the app
// hydrates without waiting on analytics. usePostHog() returns null until
// the client lands, which is the same nullable behavior callers already
// handled before this refactor.
// ---------------------------------------------------------------------------

export default function PostHogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const [client, setClient] = useState<PostHog | null>(null);

  useEffect(() => {
    if (!key || typeof window === "undefined") return;

    type IdleCallback = (deadline: { didTimeout: boolean; timeRemaining(): number }) => void;
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: IdleCallback, opts?: { timeout: number }) => number;
    }).requestIdleCallback;

    let cancelled = false;

    const start = async () => {
      // Issue 35.1 — only initialise PostHog when the user has explicitly
      // accepted. Stored in localStorage by CookieConsentBanner. The
      // `ranksector:analytics-consent-changed` listener below picks up
      // runtime accept/decline so an accept doesn't require a page
      // refresh to start capturing. Defaulting to no-op (instead of
      // opt-in) means a user who hasn't seen the banner yet doesn't
      // get captured — safer for ePrivacy-applicable visitors.
      const consent = (() => {
        try {
          return window.localStorage.getItem("ranksector.analyticsConsent");
        } catch {
          return null;
        }
      })();
      if (consent !== "accepted") {
        return;
      }
      // Lazy-import the core lib here so it splits into its own chunk
      // and stays out of the initial JS payload. The static-import
      // version (which this replaced) bundled all 178 KiB of posthog
      // into the chunk that every page downloads before first paint.
      const { default: posthog } = await import("posthog-js");
      if (cancelled) return;

      if (!posthog.__loaded) {
        posthog.init(key, {
          api_host: "/ingest",
          ui_host: "https://us.posthog.com",

          // Capture page views automatically on route change (works with
          // Next.js App Router because PostHog listens to the History API).
          capture_pageview: true,
          capture_pageleave: true,

          // Error tracking — captures unhandled JS exceptions and sends
          // them as $exception events visible in the PostHog error UI.
          capture_exceptions: true,

          // Session recording — keep off until ready to review replays.
          disable_session_recording: true,

          // PostHog autoloads ~40 KiB of JS for these features we don't
          // use (surveys.js ~33 KiB + dead-clicks-autocapture.js ~6 KiB).
          // Disabling at init prevents the bundles from loading at all.
          // Exception autocapture stays on — we rely on the error UI.
          disable_surveys: true,
          capture_dead_clicks: false,

          // Persistence via cookie so the anonymous ID survives reloads
          // and gets joined to the identified user after signup.
          persistence: "cookie",
        });
      }

      setClient(posthog);
    };

    if (typeof ric === "function") {
      ric(start, { timeout: 1000 });
    } else {
      setTimeout(start, 0);
    }

    // Bootstrap PostHog when the user accepts consent at runtime — without
    // this listener, an "accept" click on the banner only takes effect after
    // a page reload. The handler reuses `start` so we don't duplicate init
    // logic; posthog.init is idempotent (guarded by `posthog.__loaded`).
    const onConsentChange = (event: Event) => {
      const detail = (event as CustomEvent<{ choice?: string }>).detail;
      if (detail?.choice === "accepted") {
        start();
      }
    };
    window.addEventListener("ranksector:analytics-consent-changed", onConsentChange);

    return () => {
      cancelled = true;
      window.removeEventListener("ranksector:analytics-consent-changed", onConsentChange);
    };
  }, [key]);

  // Render children without the provider when the key is missing (local
  // dev / CI without PostHog configured) OR before the client has loaded.
  // usePostHog() returns null in both cases, which every caller already
  // tolerates.
  if (!key || !client) return <>{children}</>;

  return (
    <PHProvider client={client}>
      <PostHogIdentify />
      {children}
    </PHProvider>
  );
}
